import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchMyOrders, formatINR, resolveMediaUrl, cancelOrder } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, AlertCircle, CheckCircle2, Package, Truck, Ban } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  placed: { label: "Pending", color: "#F5D565", icon: Package },
  confirmed: { label: "Confirmed", color: "#F5D565", icon: CheckCircle2 },
  out_for_delivery: { label: "Out for delivery", color: "#F5D565", icon: Truck },
  delivered: { label: "Delivered", color: "#3E7A4A", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "#A3A3A3", icon: Ban },
};

const CANCEL_REASONS = [
  "Changed my mind",
  "Found a better price",
  "Ordered by mistake",
  "Other",
];

export default function MyOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [cancelling, setCancelling] = useState(null); // {order, reason}

  const load = () => fetchMyOrders().then(setOrders).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const performCancel = async (order, reason) => {
    try {
      await cancelOrder(order.id, reason);
      toast.success("Order cancelled. You'll receive an email shortly.");
      setCancelling(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel");
    }
  };

  return (
    <main data-testid="my-orders-page" className="bg-[#0A0A0A] min-h-screen pt-[100px] pb-24">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-3">
          ✦ My Orders
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl tracking-tight">
          Yours, <em className="not-italic gold-text">unboxed.</em>
        </h1>
        <p className="mt-3 font-body" style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>
          {user?.full_name ? `Signed in as ${user.full_name}` : user?.email}
        </p>

        <div className="mt-10 space-y-5" data-testid="orders-container">
          {orders === null ? (
            <p className="font-body" style={{ color: "rgba(255,255,255,0.5)" }}>Loading your orders…</p>
          ) : orders.length === 0 ? (
            <div className="border border-[#262626] p-10 text-center">
              <p className="font-serif-display italic text-2xl" style={{ color: "rgba(255,255,255,0.6)" }}>No orders yet.</p>
              <Link to="/shop" className="btn-gold mt-6 inline-block" data-testid="no-orders-shop-btn">Shop the edit</Link>
            </div>
          ) : orders.map((o) => {
            const meta = STATUS_LABELS[o.order_status] || STATUS_LABELS.placed;
            const Icon = meta.icon;
            const canCancel = o.order_status === "placed" || o.order_status === "confirmed";
            return (
              <article key={o.id} data-testid={`order-${o.order_number}`} className="border border-[#262626] p-5 md:p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-serif-display" style={{ fontSize: 20, color: "#F5D565" }}>#{o.order_number}</div>
                    <div className="font-body mt-1" style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
                      {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                      {" · "}{o.payment_method === "cod" ? "Cash on Delivery" : "Razorpay · Paid"}
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 border"
                       style={{ borderColor: meta.color, color: meta.color, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
                    <Icon size={13} strokeWidth={1.75} /> {meta.label}
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5">
                  <div className="space-y-3">
                    {o.items.map((it) => (
                      <div key={it.product_id + it.name} className="flex gap-3">
                        <img src={resolveMediaUrl(it.image)} alt="" className="w-14 h-16 object-cover bg-[#141414]" />
                        <div className="flex-1 min-w-0">
                          <div className="font-serif-display text-sm truncate">{it.name}</div>
                          <div className="text-xs text-[#A3A3A3] mt-1">Qty {it.quantity} · {formatINR(it.price)}</div>
                        </div>
                        <div className="text-sm">{formatINR(it.price * it.quantity)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="md:border-l md:border-[#262626] md:pl-5 text-right min-w-[180px]">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Total</div>
                    <div className="font-serif-display" style={{ fontSize: 26, color: "#FFFFFF" }}>{formatINR(o.total)}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] mt-3">Ship to</div>
                    <div className="font-body mt-1" style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>
                      {o.address.full_name}<br />
                      {o.address.city}, {o.address.state} {o.address.pincode}
                    </div>
                    {canCancel && (
                      <button
                        onClick={() => setCancelling({ order: o, reason: CANCEL_REASONS[0] })}
                        data-testid={`cancel-${o.order_number}`}
                        className="font-ui inline-flex items-center gap-1.5 mt-4"
                        style={{ fontSize: 11, color: "#FF9B9B", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}
                      >
                        <X size={12} strokeWidth={2} /> Cancel order
                      </button>
                    )}
                    {o.order_status === "cancelled" && o.cancel_reason && (
                      <div className="text-[11px] text-[#A3A3A3] mt-3 text-right">
                        Reason: <span style={{ color: "rgba(255,255,255,0.85)" }}>{o.cancel_reason}</span>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {/* Cancel dialog */}
      {cancelling && (
        <div data-testid="cancel-dialog" className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-md p-6 md:p-7 relative">
            <button onClick={() => setCancelling(null)} className="absolute top-4 right-4 text-[#A3A3A3] hover:text-[#F5D565]" data-testid="cancel-dialog-close">
              <X size={18} />
            </button>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.4em] uppercase" style={{ color: "#FF9B9B" }}>
              <AlertCircle size={13} /> Cancel order
            </div>
            <h2 className="font-serif-display mt-3" style={{ fontSize: 24, color: "#FFFFFF" }}>
              Cancel #{cancelling.order.order_number}?
            </h2>
            <p className="font-body mt-2" style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.55 }}>
              If applicable, refunds process in 5-7 business days. Please tell us why:
            </p>
            <div className="mt-5 space-y-2">
              {CANCEL_REASONS.map((r) => (
                <label key={r} className="flex items-center gap-2.5 cursor-pointer" data-testid={`reason-${r.replace(/\s+/g, "-").toLowerCase()}`}>
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={r}
                    checked={cancelling.reason === r}
                    onChange={() => setCancelling({ ...cancelling, reason: r })}
                    className="accent-[#F5D565]"
                  />
                  <span className="font-body text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>{r}</span>
                </label>
              ))}
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setCancelling(null)} className="flex-1 border border-[#262626] py-3 font-ui"
                      style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.85)" }}
                      data-testid="cancel-dialog-keep">
                Keep order
              </button>
              <button onClick={() => performCancel(cancelling.order, cancelling.reason)} className="btn-gold flex-1"
                      data-testid="cancel-dialog-confirm">
                Yes, cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
